import {
  type api,
  type ResponseData,
  useInfiniteApiQuery,
  useTeam,
} from '@vrplatform/data';
import { type Maybe, day, hasValue } from '@vrplatform/utils';
import type { activeStatus_enum } from '@vrplatform/graphql';
import { mapApiDepositLines } from '../edit/get-deposit-lines';
import { useDepositsTableFilter } from './useDepositsTableFilter';

const PAGE_LIMIT = 50;

export type DepositRow = {
  id: string;
  date: string;
  isOpeningBalance: boolean;
  description: string;
  status: activeStatus_enum;
  uniqueRef: string | null | undefined;
  currency: string;
  isCsv: boolean;
  isSynced: boolean;
  isReconciled: boolean;
  isPriorToStartDate: boolean;
  hasUnassignedLines: boolean;
  attachedToOwnerStatementIds: string[];
  lock:
    | (ResponseData<'get:/transactions/{id}'>['lock'] & {
        message: string | undefined;
      })
    | undefined
    | null;
  issues: ResponseData<'get:/transactions/{id}'>['issues'];
  payment: ResponseData<'get:/transactions/{id}'>['payment'];
  totals: {
    reservations: number;
    manuals: number;
    total: number;
  };
  lines: ReturnType<typeof mapApiDepositLines>['lines'];
  bankAccount:
    | {
        id: string;
        title: string;
      }
    | undefined;
  connection:
    | {
        id: string;
        name: string;
        appId: string;
        logoUrl?: string | null;
      }
    | undefined;
};

/**
 * Maps API transaction response to ExpenseRow format
 */
export function mapDepositRow(
  deposit: ResponseData<'get:/transactions'>['data'][number],
  opts: {
    statementStartAt: Maybe<string>;
  }
): DepositRow {
  const { lines, totals } = mapApiDepositLines({
    ...deposit,
    lines: deposit.lines ?? [],
  });

  const isPriorToStartDate =
    !!deposit.date &&
    !!opts.statementStartAt &&
    day(deposit.date).isBefore(opts.statementStartAt);

  const attachedToOwnerStatementIds: string[] =
    deposit.lines?.map((x) => x.ownerStatementId).filter(hasValue) ?? [];

  const isSynced =
    !!deposit.connection?.id && deposit.connection?.appId !== 'csvImport';

  return {
    id: deposit.id,
    isOpeningBalance: deposit.isOpeningBalance,
    lines,
    issues: deposit.issues,
    status: deposit.status,
    payment: deposit.payment,
    attachedToOwnerStatementIds,
    description: deposit.description,
    lock: deposit.lock
      ? {
          ...deposit.lock,
          message:
            deposit.lock.status === 'locked'
              ? `This deposit is locked and cannot be modified. ${deposit.lock.booksClosed ? `The books are closed through ${day(deposit.lock.booksClosed.date).subtract(1, 'day').format('LL')}` : ''}`
              : undefined,
        }
      : undefined,
    isCsv: deposit.connection?.appId === 'csvImport',
    isSynced,
    bankAccount: deposit.account?.id
      ? {
          id: deposit.account.id,
          title: deposit.account.name,
        }
      : undefined,
    connection: deposit.connection?.id
      ? {
          id: deposit.connection.id,
          name: deposit.connection.name,
          appId: deposit.connection.appId,
        }
      : undefined,
    date: deposit.date,
    uniqueRef: deposit.uniqueRef,
    currency: deposit.currency,
    hasUnassignedLines: !!lines.unassignedLines.length,
    isReconciled: !!deposit.payment.bankRecordIds.length,
    isPriorToStartDate,
    totals: {
      manuals: totals.manualLineCentTotal,
      reservations: totals.reservationLinesCentTotal,
      total: totals.centTotal,
    },
  };
}

type Filter = ReturnType<typeof useDepositsTableFilter>['filter'];

export const STORYBOOK_DEPOSITS_TABLE_QUERY_KEY = 'deposits-table';

export const getDepositTableQueryFilter = ({
  filter,
  limit = PAGE_LIMIT,
  page,
}: {
  filter: Filter;
  limit?: number;
  page: number;
}): NonNullable<api['/transactions']['get']['parameters']['query']> => {
  return {
    limit,
    page,
    search: filter.search?.trim() ?? undefined,
    accountId: filter.accountId ?? undefined,
    reconciled: filter.reconciled ?? undefined,
    listingIds: filter.listingIds?.join(',') ?? undefined,
    connectionId: filter.connectionId
      ? filter.connectionId === 'manual'
        ? 'null'
        : filter.connectionId
      : undefined,
    status: filter.status ?? undefined,
    type: 'deposit',
    dateRange: filter.date ?? undefined,
    isDateRangeEndInclusive: filter.date?.includes('...') ? true : undefined,
    includeLines: true,
  };
};
export const useDepositsTableQuery = () => {
  const [{ id: teamId, statementStartAt }] = useTeam();
  const { filter } = useDepositsTableFilter();

  return useInfiniteApiQuery(
    async ($api, signal, args, page) => {
      const response = await $api.GET('/transactions', {
        params: {
          query: getDepositTableQueryFilter({
            filter: args.filter,
            page,
          }),
        },
        signal,
      });

      if (response.error) {
        throw response.error;
      }

      const list = response.data.data.map((x) =>
        mapDepositRow(x, { statementStartAt: args.statementStartAt })
      );
      const connectionAppIds = Array.from(
        new Set(
          list
            .map((row) => row.connection?.appId)
            .filter(
              (appId): appId is string =>
                typeof appId === 'string' && appId.length > 0
            )
        )
      );
      const apps = await Promise.all(
        connectionAppIds.map(async (appId) => {
          const result = await $api.GET('/apps/{id}', {
            params: {
              path: {
                id: appId,
              },
            },
            signal,
          });

          if (result.error) {
            throw result.error;
          }

          return {
            id: appId,
            logoUrl: result.data.icon || null,
          };
        })
      );
      const appLogoById = new Map(
        apps.map((app) => [app.id, app.logoUrl || null] as const)
      );
      // TODO: get icon per GET /transactions?

      return {
        list: list.map((row) => ({
          ...row,
          connection: !row.connection
            ? row.connection
            : {
                ...row.connection,
                logoUrl: appLogoById.get(row.connection.appId) || null,
              },
        })),
        aggregate: response.data.pagination.total,
        pagination: response.data.pagination,
      };
    },
    {
      queryGroup: 'deposits',
      uniqueQueryKey: STORYBOOK_DEPOSITS_TABLE_QUERY_KEY,
      variables: {
        teamId,
        statementStartAt,
        filter,
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        return lastPage.pagination.nextPage ?? null;
      },
      storybookQueryKey: STORYBOOK_DEPOSITS_TABLE_QUERY_KEY,
    }
  );
};
